import React, { Fragment } from "react";
import { colors2 } from "oolib";
import TableElem from "../../../../../../components/cards/TableElem";
import { TotalCount } from "../../../TotalCount";
import DownloadDataButton from "../../DownloadDataButton";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import {
  __CheckAccessToModule,
  __CheckSomeAccessToContentType,
} from "@/utils/accessControl";
import { useSocketContext } from "@/contexts/socketioContext";
import { useFieldMigrationColumnState } from "./useFieldMigrationColumnState";

const DefaultTableWrapper = ({ children }) => (
  <div style={{ padding: "0 2rem" }}>{children}</div>
);

const TableStyle = ({
  tableConfig: tableConfig_,
  builderProps,
  data: data_,
  status,
  isFetchingNextPage,
  observerRef,
  totalCount,
  activeSort,
  setActiveSort,
  enableContainerQuery,
  inTCI,
  getDataToBeExported,
  actionsConfig,
  conditionsConfig,
  handleRowClick,
}) => {
  let tableConfig = tableConfig_;
  let data = data_;

  const userData = useGetQueryData('userData');
  const user = userData?.user;
  const { SELF_MANAGED_BASE_CONFIGS, ai } = useGetQueryData('platformConfigs') || {};
  // Server-derived tracker; same source the dashboard AIAnnotationWidget
  // uses. Survives reloads / other tabs because it's the BullMQ queue state,
  // not ephemeral client state. Each in-flight run lists the documents it
  // still has pending — that per-document set is what gates annotation here
  // (the run itself is never a reason to block the rest of the dataset).
  const { segByAutoAnnotateIdJobs } = useSocketContext() || {};

  // Columns whose field-type-change migration is anywhere between "API call
  // fired" and "migrated rows refetched" — fully derived (react-query mutation
  // state + socket tracker) in the hook.
  const listingContentType =
    tableConfig.contentType || data?.[0]?.meta?.kp_content_type;
  const migratingValuePaths = useFieldMigrationColumnState(
    SELF_MANAGED_BASE_CONFIGS ? listingContentType : undefined
  );

  if (SELF_MANAGED_BASE_CONFIGS) {
    tableConfig = {
      ...tableConfig,
      // 'subtitle' = the block's props.id, which is what tpl-extracted
      // colIds are derived from (SM tpls always carry props.id)
      hiddenColIds: [...(tableConfig.hiddenColIds || []), 'subtitle'],
    };
  }

  // Listing-table bulk actions, each gated to mirror the BE middleware:
  //   - aiAnnotate -> moduleAccess(["PUBLISH"], "highlights") + aiAnnotationEnabled
  //                   platformConfig (self-managed only)
  //   - bulkDelete -> contentAccess(["DELETE","MODERATE"])     (all tenants)
  {
    // tableConfig.contentType is only set by the genBaseTableConfig default;
    // a tpl-provided TableStyle listingConfig may omit it. Fall back to the row
    // data — the same source the delete operation derives contentType from.
    const ctForAccess = tableConfig.contentType || data?.[0]?.meta?.kp_content_type;
    const canAnnotate =
      SELF_MANAGED_BASE_CONFIGS &&
      ai?.aiAnnotation?.enable === true &&
      !!user && __CheckAccessToModule(user, 'PUBLISH', 'highlights');
    const canBulkDelete =
      !!user &&
      !!ctForAccess &&
      __CheckSomeAccessToContentType(user, ['DELETE', 'MODERATE'], ctForAccess);

    // Documents of THIS listing's contentType that an in-flight run still has
    // pending. Those rows can't be selected (TableRowElem / header select-all
    // honour tableConfig.disabledRowIds); every other row stays annotatable
    // while the run proceeds. The backend applies the same per-document
    // exclusion on submit, so a stale tracker can't double-queue a document.
    const inFlightDocIds = [];
    if (ctForAccess && segByAutoAnnotateIdJobs) {
      for (const run of Object.values(segByAutoAnnotateIdJobs)) {
        if (run?.contentType !== ctForAccess) continue;
        const status = Object.values(run?.queues || {})[0]?.status;
        if (status !== 'autoAnnotating') continue;
        for (const d of run?.docs?.pending || []) {
          if (d?._id) inFlightDocIds.push(String(d._id));
        }
      }
    }

    const bulkActions = [
      ...(canAnnotate ? [{ display: 'Run AI Annotation', action: 'aiAnnotate' }] : []),
      ...(canBulkDelete ? [{ display: 'Delete', action: 'bulkDelete' }] : []),
    ];

    if (bulkActions.length > 0) {
      tableConfig = {
        ...tableConfig,
        bulkActions: [
          ...(tableConfig.bulkActions || []),
          ...bulkActions,
        ],
      };
    }
    if (inFlightDocIds.length > 0) {
      tableConfig = {
        ...tableConfig,
        disabledRowIds: inFlightDocIds,
        disabledRowLabel: 'Annotating…',
      };
    }
  }

  // Self-managed listings layer a custom title cell:
  //  - leading "annotated" icon + count badge if the doc has annotations
  //    (data.annotationCount, derived server-side via the
  //    includeAnnotationCount flag — distinct annotation count from the
  //    annotations.tags.tags.data rollup)
  //  - trailing "Under review" / "Under edit" tag (gated on
  //    showUnderEditInfoOnCardOrListItem)
  // Both behaviours live on a SINGLE title cellData entry because the
  // tableConfig merge replaces `props` shallowly per colId — layering two
  // entries would clobber whichever ran first. colId 'title' = the title
  // block's props.id, which tpl-extracted colIds are derived from.
  if (SELF_MANAGED_BASE_CONFIGS) {
    const showUnderEditInfo =
      SELF_MANAGED_BASE_CONFIGS?.listingConfig
        ?.showUnderEditInfoOnCardOrListItem === true;

    tableConfig = {
      ...tableConfig,
      cellData: [
        ...(tableConfig.cellData || []),
        {
          colId: 'title',
          comp: 'TableTextWithTag',
          props: {
            leadingIcons: [
              {
                // value > 0 → pill with icon + count; 0/undefined → hidden
                valuePath: 'annotationCount',
                tooltip: 'Annotated',
                icon: {
                  name: 'HighlighterCircle',
                  // icon + count foreground in pill mode
                  color: colors2.greenText,
                  size: 13,
                },
                // pill background
                badgeBgColor: colors2.greenBG,
              },
            ],
            ...(showUnderEditInfo
              ? {
                  tagValuePath: 'underEditInfoText',
                  labelValuePath: 'underEditInfoLabel',
                  colorPreset: 'warning',
                }
              : {}),
          },
        },
      ],
    };

    if (showUnderEditInfo) {
      data = data?.map((content) => {
        const infoText =
          content.kp_published_status === "underModeration"
            ? `Under review by ${content?.kp_moderator?.name}`
            : !!content.editPublishedDocId && !!content.currentEditor
            ? `Under edit by ${content?.meta?.kp_contributed_by?.name}`
            : undefined;

        const infoLabel =
          content.kp_published_status === "underModeration"
            ? `Under review`
            : !!content.editPublishedDocId && !!content.currentEditor
            ? `Under edit`
            : undefined;

        return {
          ...content,
          ...(infoText ? { underEditInfoText: infoText } : {}),
          ...(infoLabel ? { underEditInfoLabel: infoLabel } : {}),
        };
      });
    }
  }

  // Swap each migrating column to a static "Updating data…" cell and kill its
  // sort — matched by the column's exact valuePath, as a patch-only merge
  // entry (no colId derivation, no phantom column if the tpl hasn't caught
  // up). The override drops away when the hook releases the column, and the
  // tpl-derived config (already carrying the NEW comp) renders naturally.
  if (SELF_MANAGED_BASE_CONFIGS && migratingValuePaths.size > 0) {
    tableConfig = {
      ...tableConfig,
      cellData: [
        ...(tableConfig.cellData || []),
        ...[...migratingValuePaths].map((valuePath) => ({
          valuePath,
          comp: "TableStaticText",
          props: { text: "Updating data…", color: colors2.grey60 },
          // Shallow merge overwrites the extracted entries — explicitly
          // undefined kills both FE + BE sorting for the column.
          sortConfig: undefined,
          backendSortConfig: undefined,
        })),
      ],
    };
  }

  return (
    <Fragment>
      {totalCount !== undefined && (
        <div enableContainerQuery={enableContainerQuery}>
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              paddingBottom: "1rem",
              paddingLeft: "2rem",
            }}
          >
            <TotalCount totalCount={totalCount} />
            {getDataToBeExported && (
              <>
                <DownloadDataButton getDataToBeExported={getDataToBeExported} />
              </>
            )}
          </div>
        </div>
      )}
      {totalCount !== 0 && (
        <TableElem
          TableWrapper={DefaultTableWrapper}
          actionsConfig={actionsConfig}
          conditionsConfig={conditionsConfig}
          tableConfig={tableConfig}
          data={data}
          status={status}
          lazyLoad={{
            isFetchingNextPage,
            observerRef,
            backendActiveSort: activeSort,
            setBackendActiveSort: setActiveSort,
          }}
          builderProps={builderProps?.TableStyle}
          enableContainerQuery={enableContainerQuery}
          handleRowClick={handleRowClick}
          /* ⚠️ CUSTOM — the ONLY place this prop is meant to be set. It exists
             for this one use case: the SM-only per-column "change field type"
             header control (standalone changeFieldComp endpoint). It is not a
             general TableElem API — don't pass it from anywhere else.
             Undefined for non-SM tenants, which is what keeps the control off
             their tables. Same contentType fallback the bulk actions above use
             (tableConfig.contentType, else row data). */
          custom_listingPageChangeFieldCompProps={
            SELF_MANAGED_BASE_CONFIGS && listingContentType
              ? {
                  contentType: listingContentType,
                  // Mirror of the BE's migration quiescence guard (which 409s
                  // a type change while one is running for this contentType) —
                  // the pencil disables through the ENTIRE "updating" window
                  // the hook derives (API in flight → migration → refetch).
                  fieldTypeChangeDisabled: migratingValuePaths.size > 0,
                }
              : undefined
          }
        />
      )}
    </Fragment>
  );
};

export default TableStyle;
